import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as _ from 'lodash-es';
import { JsonObjectFactoryService } from '../../../shared/_services/json-object-factory.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { BatchOpenAmendedService } from './batch-open-amended.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-batch-open-amended',
    templateUrl: './batch-open-amended.component.html',
    styleUrls: ['./batch-open-amended.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class BatchOpenAmendedComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();
  private readonly subs = new Subscription();

  filterParams: any = {};
  rowData: any = {};
  baseURLs: any = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};

  // Component properties
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  
  // Business logic properties
  tax_year: string = '';
  scenario: string = '';
  jcd_key: string = '';
  logged_in_user: string = '';
  modalName: string = 'Batch Open in Amended';
  
  isSaveClicked: boolean = false;
  isProcessing: boolean = false;
  syncSaveDtls: any[] = [];
  gridDataMultipleSelect: any = {};
  allocationLimit: number = 0;

  // Modal configuration following fir163jsync pattern
  showModal: boolean = false;
  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to Batch Open the Selected Partnerships in amended cycle?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private gtwSnackbarService: GtwSnackbarService,
    private jsonObjectFactory: JsonObjectFactoryService,
    private batchOpenAmendedService: BatchOpenAmendedService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.processGridData();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.processGridData();
      this.init();
    }));
    this.init();
    this.updateModalConfig();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
  }

  init(): void {
    if (Object.keys(this.filterParams).length > 0 && Object.keys(this.baseURLs).length > 0) {
      this.initializeProperties();
      this.fetchMaxCountEntities();
      this.updateModalConfig();
    }
  }

  updateModalConfig(): void {
    // Update modal message based on current state
    let message = 'Are you sure you want to Batch Open the Selected Partnerships in amended cycle?';
    
    // Add validation messages if needed
    if (this.getSelectedCount() === 0) {
      message = 'No partnerships selected for processing.';
      this.modalConfig.showConfirmButton = false;
    } else if (this.getAllocationLimit() > 0 && this.getSelectedCount() > this.getAllocationLimit()) {
      message = `Cannot select more than ${this.getAllocationLimit()} partnerships. Currently selected: ${this.getSelectedCount()}`;
      this.modalConfig.showConfirmButton = false;
    } else {
      this.modalConfig.showConfirmButton = true;
    }

    this.modalConfig = {
      ...this.modalConfig,
      message: message,
      loading: this.isProcessing
    };
  }

  initializeProperties(): void {
    // 🚀 CRITICAL: Use real data from filterParams and globalService (NOT hardcoded)
    this.tax_year = this.filterParams.tax_year || '';
    this.scenario = this.filterParams.scenario || '';
    this.jcd_key = this.globalService?.jcd_key || '';
    this.logged_in_user = this.USER_SETTINGS?.user?.sso_id || '';
  }

  processGridData(): void {
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.gridDataMultipleSelect = _.clone(this.rowData);
      this.syncSaveDtls = [];

      // Process grid data to match exact format: [{"ho_le_key":1703322},{"ho_le_key":1693527}]
      if (this.gridDataMultipleSelect) {
        for (let key in this.gridDataMultipleSelect) {
          let gridObj: any = {};
          // Extract ho_le_key from the data structure
          const hoLeKey = this.gridDataMultipleSelect[key].data?.HO_LE_KEY || 
                         this.gridDataMultipleSelect[key].HO_LE_KEY;
          
          if (hoLeKey) {
            gridObj["ho_le_key"] = hoLeKey;
            this.syncSaveDtls.push(gridObj);
          }
        }
      }
      
      // Update modal configuration after processing grid data
      this.updateModalConfig();
    }
  }

  fetchMaxCountEntities(): void {
    if (!this.GENERAL_CONFIG.base_url || !this.globalService?.screen_key) {
      return;
    }

    this.batchOpenAmendedService.fetchMaxCountEntities(
      this.GENERAL_CONFIG.base_url,
      this.globalService.screen_key,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject && response.jsonObject.length > 0) {
          this.allocationLimit = parseInt(response.jsonObject[0].ATTRIB_VALUE) || 0;
        }
      },
      (error: any) => {
        console.error('Error fetching max count entities:', error);
        this.allocationLimit = 50; // Default fallback
      }
    );
  }

  confirm_sync(): void {
    // Validate allocation limit
    if (this.syncSaveDtls.length > this.allocationLimit && this.allocationLimit > 0) {
      this.gtwSnackbarService.openSnackBar(`Can't select more than ${this.allocationLimit} partnerships.`, 'gtw-snackbar--red');
      return;
    }

    this.isProcessing = true;
    this.updateModalConfig(); // Update modal to show loading state

    // 🚀 CRITICAL: Build settings object to match exact format specified
    let returnClobSettingsObj: any = {};
    returnClobSettingsObj['tax_year'] = this.tax_year;
    returnClobSettingsObj['scenario'] = this.scenario;
    returnClobSettingsObj['jcd_key'] = this.jcd_key;
    returnClobSettingsObj['sso_id'] = this.logged_in_user;

    let ecSaveDtls = this.syncSaveDtls;
    
    if (this.syncSaveDtls && this.syncSaveDtls.length > 0) {
      let message = "Selected Partnerships Submitted for Bulk Open in Amended Cycle";
      this.sendDetails(returnClobSettingsObj, ecSaveDtls, message);
    } else {
      this.isProcessing = false;
      this.gtwSnackbarService.openSnackBar('No partnerships selected for processing.', 'gtw-snackbar--red');
    }
  }

  sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    // 🚀 CRITICAL: Pass filterParams and rowData to service
    this.batchOpenAmendedService.batchOpenPshipsInAmended(
      this.GENERAL_CONFIG.base_url,
      returnClobSettingsObj,
      ecSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isProcessing = false;
        this.updateModalConfig(); // Update modal to hide loading state
        
        if (response.errorMessage && response.errorMessage !== 'null') {
          this.isSaveClicked = false;
          this.gtwSnackbarService.openSnackBar(response.errorMessage, 'gtw-snackbar--red');
        } else {
          // 🚀 CRITICAL: Show success message and emit 'refresh' string
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          
          // 🚀 CRITICAL: Emit 'refresh' string (NOT object)
          this.requestGridUpdate.emit('refresh');
          // NO this.cancel() call - AngularJS handles modal close automatically
          this.showModal = false; // Hide modal after successful operation
        }
      },
      (error: any) => {
        this.isProcessing = false;
        this.isSaveClicked = false;
        this.updateModalConfig(); // Update modal to hide loading state
        this.gtwSnackbarService.openSnackBar('Error occurred while processing. Please try again.', 'gtw-snackbar--red');
      }
    );
  }

  // Modal event handlers following fir163jsync pattern
  onConfirm(): void {
    this.confirm_sync();
  }

  onCancel(): void {
    this.cancel();
  }

  cancel(): void {
    // Following fir163jsync pattern - dispatch CustomEvent to close modal
    this.showModal = false;
    
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-batch-open-amended-ce')) {
      document.querySelector('gtw-batch-open-amended-ce')?.dispatchEvent(updateEvent);
    }
  }

  getSelectedCount(): number {
    return this.syncSaveDtls ? this.syncSaveDtls.length : 0;
  }

  getAllocationLimit(): number {
    return this.allocationLimit || 0;
  }
}
